import { createApiClient } from '../../shared/http/createApiClient.js';

const client = createApiClient({ auth: true });

// An attachment goes browser -> Blob Storage directly, so it cannot use the JSON client above.
// XHR rather than fetch because fetch still has no upload progress event, and a paste that
// shows nothing while a 2MB PNG travels reads as a paste that did not work.
//
// Copied from competitive-monitoring/api.js rather than shared, per the module convention, with
// the content type added: without x-ms-blob-content-type the blob is stored as
// application/octet-stream and the browser downloads it instead of rendering the thumbnail.
//
// The `screenshots` in the route names below is history — the endpoints took images before they
// took anything else, and renaming a live route and its table buys nothing this note does not.
function uploadToBlob(url, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('x-ms-blob-content-type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Upload failed: network error'));
    xhr.send(file);
  });
}

export const devApi = {
  listTasks:   (params)      => client.get('/orbit-dev/tasks', { params }),
  // Separate from listTasks because it signs a read URL per attachment, which is a cost only an
  // export should pay. `statuses` is a comma-separated list — the caller names them because the
  // views are defined in the frontend and the server has no business knowing what My Work is.
  exportTasks: (params)      => client.get('/orbit-dev/tasks/export', { params }),
  getTask:     (id)          => client.get(`/orbit-dev/tasks/${id}`),
  createTask:  (body)        => client.post('/orbit-dev/tasks', body),
  updateTask:  (id, body)    => client.patch(`/orbit-dev/tasks/${id}`, body),
  deleteTask:  (id)          => client.del(`/orbit-dev/tasks/${id}`),

  // The only route that changes a lifecycle status. A PATCH carrying `lifecycle_status` is
  // refused by the server on purpose: every change has to leave a row in the transition log,
  // and a second way in is a change that quietly does not.
  transitionTask: (id, toStatus, note = null) =>
    client.post(`/orbit-dev/tasks/${id}/transition`, { to_status: toStatus, note }),

  listSections:   ()         => client.get('/orbit-dev/sections'),
  createSection:  (name)     => client.post('/orbit-dev/sections', { name }),
  renameSection:  (id, name) => client.patch(`/orbit-dev/sections/${id}`, { name }),
  deleteSection:  (id)       => client.del(`/orbit-dev/sections/${id}`),
  setSectionsOrder: (sectionIds) =>
    client.put('/orbit-dev/sections/order', { section_ids: sectionIds }),
  // A drop names the neighbour it landed above, never a position — see the route's docstring.
  moveTaskToSection: (taskId, sectionId, beforeTaskId = null) =>
    client.put(`/orbit-dev/tasks/${taskId}/section`,
      { section_id: sectionId, before_task_id: beforeTaskId }),

  reserveAttachment: (taskId, body) =>
    client.post(`/orbit-dev/tasks/${taskId}/screenshots`, body),
  commitAttachment:  (taskId, sid) =>
    client.post(`/orbit-dev/tasks/${taskId}/screenshots/${sid}/commit`),
  deleteAttachment:  (taskId, sid) =>
    client.del(`/orbit-dev/tasks/${taskId}/screenshots/${sid}`),

  uploadToBlob,
};

/**
 * Reserve, PUT, commit — the three steps an attachment takes, kept together because getting
 * one of them out of order leaves a row with no blob or a blob with no row.
 */
export async function uploadAttachment(taskId, file, onProgress) {
  const { screenshot, upload_url } = await devApi.reserveAttachment(taskId, {
    file_name: file.name || 'attachment',
    content_type: file.type || 'application/octet-stream',
    byte_size: file.size,
  });
  await devApi.uploadToBlob(upload_url, file, onProgress);
  const done = await devApi.commitAttachment(taskId, screenshot.id);
  return done.screenshot;
}
